/**
 * Philanthropy Page - "Isn't That Dandy!" Gala Concept
 * A Celebration of Life, Style & the Kidney Journey
 * Proposal Deck for Founding Committee & Strategic Partners
 * Proposed Date: Saturday, April 19, 2026
 */

import { useState } from "react";
import { 
  Heart, 
  Users, 
  Building2, 
  MapPin, 
  Sparkles, 
  Shield, 
  Globe, 
  Target,
  ChevronDown,
  ChevronUp,
  Star,
  Palette,
  Music,
  Crown,
  Church,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle2,
  Mic,
  Award,
  TrendingUp,
  FileText,
  Scale,
  PartyPopper
} from "lucide-react";

// NEW AI-Generated Diverse Gala Images
const DIVERSITY_IMAGES = {
  southernBelle: "https://static.prod-images.emergentagent.com/jobs/f22708ce-8526-41bb-b805-57f2417c36a5/images/77761480a42d2343c96c4105fcd09974802f380b5c2b56616eb01d36ecd6b062.png",
  whiteFormal: "https://static.prod-images.emergentagent.com/jobs/f22708ce-8526-41bb-b805-57f2417c36a5/images/945d840f7913d28c170296678c49cd32e98a8309c7c28acf1a7756eeb5743412.png",
  africanFormal: "https://static.prod-images.emergentagent.com/jobs/f22708ce-8526-41bb-b805-57f2417c36a5/images/219cd9576e092aff6acb2da367827a9865c61c16d7a7f9cc828dd0b172da73aa.png",
  latinoFormal: "https://static.prod-images.emergentagent.com/jobs/f22708ce-8526-41bb-b805-57f2417c36a5/images/9f93e7c66adc0a01756680ac2ad1626df676f56142020d187337391bd09fdea6.png",
  asianFormal: "https://static.prod-images.emergentagent.com/jobs/f22708ce-8526-41bb-b805-57f2417c36a5/images/4ac3b05de984e9654ec328a0e25c5f3d5f88fb3e7a150598bb8ff948ce78064a.png",
  ballroomSetup: "https://static.prod-images.emergentagent.com/jobs/f22708ce-8526-41bb-b805-57f2417c36a5/images/79ec58c15705ece6a5cb160e22a51d231426d11fd3634360d0e1eaa16e100a95.png",
  redCarpet: "https://static.prod-images.emergentagent.com/jobs/f22708ce-8526-41bb-b805-57f2417c36a5/images/3b02d029d445a251fdbed60fd1d5b9a844ea0fd87c01e5333abd88f7f0df85a7.png"
};

// Original stock images for venues
const VENUE_IMAGES = {
  tmc3: "https://images.unsplash.com/photo-1770824906466-6254ca2cdc14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtZWRpY2FsJTIwY2VudGVyJTIwaGVsaXglMjBnYXJkZW4lMjBvdXRkb29yJTIwZXZlbnQlMjBzcGFjZXxlbnwwfHx8fDE3NzE2OTc0Njh8MA&ixlib=rb-4.1.0&q=85",
  ulh: "https://images.pexels.com/photos/12689009/pexels-photo-12689009.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  temple: "https://images.pexels.com/photos/31087178/pexels-photo-31087178.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
};

// Stakeholder data from PowerPoint
const STAKEHOLDERS = [
  {
    name: "Harris Health – Quinton Mease Dialysis",
    category: "Dialysis Partner",
    icon: Building2,
    description: "Recognizes their unwavering commitment to the CKD community and frontline dialysis care—serving patients with dignity across Greater Houston's most underserved populations. Infection control & water systems legacy partnership.",
    color: "from-blue-500 to-cyan-500"
  },
  {
    name: "St. Luke's + Transplant Centers",
    category: "Transplant Partner",
    icon: Heart,
    description: "Patient pathway: access, education, outcomes. Represents both a personal patient journey (transplant list) and a future-focused professional expansion of Sterile Mate LLC's healthcare footprint.",
    color: "from-rose-500 to-pink-500"
  },
  {
    name: "Transplant Centers of Greater Houston",
    category: "Clinical Authority",
    icon: Target,
    description: "Provide the clinical authority, legitimacy, and regional leadership necessary for a signature, city-wide medical-philanthropic event.",
    color: "from-emerald-500 to-teal-500"
  },
  {
    name: "Churches & Community Health Programs",
    category: "Community Anchors",
    icon: Church,
    description: "Anchor the event in the lived reality of kidney disease, particularly its disproportionate impact on Black, Brown, and immigrant communities. Screenings, education, navigation support.",
    color: "from-amber-500 to-orange-500"
  },
  {
    name: "Pharmaceutical & Medical Manufacturers",
    category: "Industry Sponsors",
    icon: Shield,
    description: "Strategic, mission-aligned sponsors (e.g., Amgen, Baxter) with both resources and responsibility to support patient outcomes across dialysis and transplant care. Outcome-aligned investments.",
    color: "from-purple-500 to-violet-500"
  }
];

// Venue data from PowerPoint
const VENUES = [
  {
    name: "TMC3 – Helix Gardens",
    tagline: "Flagship Innovation & Prestige",
    description: "Iconic medical-future setting. Premium sponsor appeal. Media-ready 'signature' look. Best for: national sponsors + press.",
    image: VENUE_IMAGES.tmc3,
    features: ["Innovation Hub", "Elite Philanthropy", "Medical Leadership", "National Sponsors"]
  },
  {
    name: "ULH Event Center – North Side",
    tagline: "Community-Accessible Sophistication",
    description: "Grounded in local reality. Strong attendance potential. Scalable production footprint. Best for: maximum attendance.",
    image: VENUE_IMAGES.ulh,
    features: ["Community Access", "Elegant Setting", "High Capacity", "Local Impact"]
  },
  {
    name: "Shiva Temple – Missouri City",
    tagline: "Architectural Beauty & Cultural Inclusion",
    description: "Powerful multicultural statement. Unforgettable visuals. Authentic community bridges. Best for: cultural statement venue.",
    image: VENUE_IMAGES.temple,
    features: ["Architectural Beauty", "Multicultural", "Global Perspective", "Cultural Statement"]
  }
];

// Sponsorship Tiers from PowerPoint
const SPONSORSHIP_TIERS = [
  { tier: "Title / Presenting", investment: "$50,000", highlights: "Naming, stage recognition, prime visibility" },
  { tier: "Platinum", investment: "$35,000", highlights: "Prime placement, VIP experience, lounge activation" },
  { tier: "Gold", investment: "$15,000", highlights: "Program + step-and-repeat visibility" },
  { tier: "Silver", investment: "$7,500", highlights: "Program listing + partner recognition" },
  { tier: "Table Sponsor", investment: "$10,000+", highlights: "10 seats + branded table identity" }
];

// Financial Projections from PowerPoint
const FINANCIAL_PROJECTIONS = {
  revenue: [
    { item: "General Admission (700 × $1,000)", amount: "$700,000" },
    { item: "VIP (100 × $2,000)", amount: "$200,000" },
    { item: "Table Sponsors (20 × $10,000)", amount: "$200,000" },
    { item: "Title/Premium Sponsors (5 × $50,000)", amount: "$250,000" },
    { item: "Cultural Grants & Foundations", amount: "$100,000" }
  ],
  expenses: [
    { item: "Venue + Décor", amount: "$100,000" },
    { item: "Entertainment & Talent", amount: "$250,000" },
    { item: "Catering + Bar", amount: "$150,000" },
    { item: "AV / Stage Production", amount: "$75,000" },
    { item: "Marketing + PR", amount: "$60,000" },
    { item: "Event Production Firm", amount: "$50,000" },
    { item: "Salaries (Creator + PM + Committee + Staff)", amount: "$275,000" },
    { item: "Insurance / Security / Permits", amount: "$20,000" },
    { item: "Contingency", amount: "$29,000" }
  ],
  totals: {
    revenue: "$1,450,000",
    expenses: "$1,009,000",
    netProceeds: "$441,000"
  }
};

// Timeline data from PowerPoint
const TIMELINE = [
  {
    phase: "Months 1–2",
    title: "Foundation",
    items: ["Confirm venue/date", "Form founding committee", "Governance + ethical firewall", "Sponsor deck + outreach list"]
  },
  {
    phase: "Months 3–5",
    title: "Partnerships",
    items: ["Clinical + community partners", "Entertainment shortlist", "Secure title sponsor", "Launch save-the-date"]
  },
  {
    phase: "Months 6–8",
    title: "Production",
    items: ["Finalize run of show", "Vendor contracts", "Marketing rollout", "Auction + pledge build"]
  },
  {
    phase: "Months 9–12",
    title: "Execute",
    items: ["Rehearsals + walk-throughs", "Final sales push", "Event week operations", "Post-event reporting + distribution"]
  }
];

// Expandable section component
const ExpandableSection = ({ title, icon: Icon, children, defaultOpen = false, badgeText = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white hover:from-slate-100 hover:to-slate-50 transition-colors"
        data-testid={`expand-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center shadow-lg">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
          {badgeText && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
              {badgeText}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 py-5 border-t border-slate-100 animate-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};

export default function PhilanthropyPage() {
  return (
    <div className="space-y-8 pb-12" data-testid="philanthropy-page">
      {/* Hero Section */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={DIVERSITY_IMAGES.redCarpet} 
            alt="Elegant Gala Red Carpet Arrival" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-rose-950/80 to-amber-950/70" />
        </div>
        
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-rose-500/20 to-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-amber-500/15 to-rose-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center shadow-lg shadow-rose-500/30">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <span className="px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-amber-300 text-sm font-medium border border-amber-400/30">
              Philanthropic Initiative
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            ISN'T THAT <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-rose-400 to-amber-400">DANDY!</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-rose-200/90 font-light mb-2">
            A Celebration of Life, Style & the Kidney Journey
          </p>
          
          <p className="text-amber-200/70 text-lg mb-4">
            Proposal Deck for Founding Committee & Strategic Partners
          </p>
          
          <div className="flex flex-wrap gap-4 text-sm text-white/80">
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
              <Calendar className="w-4 h-4 text-amber-400" />
              <span>Saturday, April 19, 2026</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
              <Users className="w-4 h-4 text-amber-400" />
              <span>Target: 1,000 Guests</span>
            </div>
          </div>
        </div>
      </div>

      {/* Diverse Cultural Gallery - "Houstonian Texas Formal" */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          Celebrating Houston's Diversity
        </h3>
        <p className="text-slate-400 text-sm mb-4">Luxury, culture, and impact — built with dignity and shared purpose</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="aspect-square rounded-xl overflow-hidden">
            <img src={DIVERSITY_IMAGES.southernBelle} alt="Southern Belle & Gentleman" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
          </div>
          <div className="aspect-square rounded-xl overflow-hidden">
            <img src={DIVERSITY_IMAGES.whiteFormal} alt="Elegant Formal Couple" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
          </div>
          <div className="aspect-square rounded-xl overflow-hidden">
            <img src={DIVERSITY_IMAGES.africanFormal} alt="African American Formal" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
          </div>
          <div className="aspect-square rounded-xl overflow-hidden">
            <img src={DIVERSITY_IMAGES.asianFormal} alt="Asian Formal" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
          </div>
          <div className="aspect-square rounded-xl overflow-hidden">
            <img src={DIVERSITY_IMAGES.latinoFormal} alt="Latino Formal" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <ExpandableSection title="Executive Summary" icon={FileText} defaultOpen={true}>
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-rose-50 to-amber-50 rounded-xl p-6 border border-rose-100">
            <p className="text-rose-800 font-medium text-lg italic mb-0">
              "A signature gala that celebrates life and builds a city-wide kidney journey coalition."
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-slate-800 mb-3">What This Is</h4>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>A fashion-forward, high-energy gala celebrating resilience across dialysis-to-transplant care</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>A partnership platform connecting health systems, transplant centers, community programs, and industry sponsors</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                  <span>A media-ready cultural moment that treats patients and families with dignity, not pity</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-800 mb-3">What Success Looks Like (Year 1)</h4>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <Star className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  <span>1,000 attendees with strong C-suite + community representation</span>
                </li>
                <li className="flex items-start gap-2">
                  <Star className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  <span>Top-tier entertainment line-up with culturally relevant dance & music</span>
                </li>
                <li className="flex items-start gap-2">
                  <Star className="w-4 h-4 text-amber-500 mt-1 flex-shrink-0" />
                  <span>Documented impact plan: education, screening, patient support, and transplant access</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="bg-slate-100 rounded-xl p-4">
            <h4 className="font-semibold text-slate-800 mb-2">Why Now?</h4>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <Heart className="w-4 h-4 text-rose-500" />
                <span>Patients deserve celebration—not only clinical narratives</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Globe className="w-4 h-4 text-blue-500" />
                <span>Houston's scale and diversity demand an inclusive, city-wide approach</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                <span>Industry sponsors are seeking credible, measurable community impact</span>
              </div>
            </div>
          </div>
        </div>
      </ExpandableSection>

      {/* Strategic Stakeholders */}
      <ExpandableSection title="Strategic Stakeholders: The Kidney Journey Ecosystem" icon={Users} defaultOpen={true}>
        <p className="text-slate-600 mb-2">
          A full "Kidney Journey" ecosystem — from dialysis to transplant and community prevention.
        </p>
        <p className="text-slate-500 text-sm mb-6 italic">
          Design principle: one room where C-suite leaders and community champions sit side-by-side—with dignity and shared purpose.
        </p>
        
        <div className="grid gap-4">
          {STAKEHOLDERS.map((stakeholder, index) => (
            <div 
              key={index}
              className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-slate-50 to-white border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all"
              data-testid={`stakeholder-${index}`}
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stakeholder.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                <stakeholder.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-slate-800">{stakeholder.name}</h4>
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{stakeholder.category}</span>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">{stakeholder.description}</p>
              </div>
            </div>
          ))}
        </div>
      </ExpandableSection>

      {/* Venue Strategy */}
      <ExpandableSection title="Venue Strategy: 'Diversity in Luxury'" icon={MapPin} defaultOpen={true}>
        <p className="text-slate-600 mb-6 text-lg">
          Three venue options that reinforce the message: <span className="font-semibold text-slate-800">kidney disease touches every community.</span>
        </p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {VENUES.map((venue, index) => (
            <div 
              key={index}
              className="group relative rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-xl transition-all duration-300"
              data-testid={`venue-${index}`}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={venue.image} 
                  alt={venue.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <p className="text-amber-300 text-sm font-medium mb-1">{venue.tagline}</p>
                <h4 className="font-bold text-lg mb-2">{venue.name}</h4>
              </div>
              
              <div className="p-4 bg-white">
                <p className="text-slate-600 text-sm mb-3">{venue.description}</p>
                <div className="flex flex-wrap gap-2">
                  {venue.features.map((feature, i) => (
                    <span key={i} className="px-2 py-1 bg-slate-100 rounded-full text-xs text-slate-600 font-medium">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Venue Ambiance */}
        <div className="mt-8">
          <h4 className="text-slate-700 font-semibold mb-4">Venue Ambiance Inspiration</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
              <img src={DIVERSITY_IMAGES.ballroomSetup} alt="Luxury Ballroom Setup" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
            <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
              <img src={DIVERSITY_IMAGES.redCarpet} alt="Red Carpet Arrival" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
          </div>
        </div>
      </ExpandableSection>

      {/* Event Experience */}
      <ExpandableSection title="Event Experience" icon={PartyPopper} defaultOpen={true}>
        <p className="text-slate-600 mb-6">Met-Gala energy with a mission — celebration-first, impact always.</p>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-5 border border-rose-100">
            <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Star className="w-5 h-5 text-rose-500" />
              Arrival & Welcome
            </h4>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>Red carpet portraits + press wall</li>
              <li>Cocktail reception</li>
              <li>Cultural style showcases</li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-100">
            <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Mic className="w-5 h-5 text-amber-500" />
              Main Program
            </h4>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>Storytelling moments from patients & clinicians</li>
              <li>Live performances (music + dance)</li>
              <li>Impact pledge + auction</li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-5 border border-violet-100">
            <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Music className="w-5 h-5 text-violet-500" />
              Afterglow
            </h4>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>DJ + live band set</li>
              <li>Lounge activations for sponsors</li>
              <li>Networking across ecosystems</li>
            </ul>
          </div>
        </div>
      </ExpandableSection>

      {/* Entertainment Vision */}
      <ExpandableSection title="Entertainment Vision" icon={Music}>
        <p className="text-slate-600 mb-6">A relevant, inclusive program — local excellence + international prestige.</p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-slate-800 mb-3">Programming Goals</h4>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                <span>One marquee headliner to anchor press and ticket demand</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                <span>Culturally representative dance + music groups across communities served</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                <span>A program that feels joyful, elevated, and deeply respectful</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                <span>A/V production that matches the venue and guest expectation</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-indigo-900 to-violet-900 rounded-xl p-5 text-white">
            <h4 className="font-bold mb-3">Entertainment Budget (Year 1)</h4>
            <div className="space-y-4">
              <div>
                <p className="text-indigo-200 text-sm">Target Allocation</p>
                <p className="text-3xl font-bold text-white">$250,000</p>
                <p className="text-indigo-300 text-sm">Covers headliner, cultural acts, and show production needs</p>
              </div>
              <div className="border-t border-white/20 pt-4">
                <p className="text-indigo-200 text-sm mb-2">Talent Mix (example)</p>
                <ul className="text-sm space-y-1 text-white/80">
                  <li>Headliner vocalist (national / international)</li>
                  <li>Cultural dance ensemble(s)</li>
                  <li>Local jazz / gospel / Afrobeat sets</li>
                  <li>Afterglow DJ + live band</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </ExpandableSection>

      {/* Sponsorship Model */}
      <ExpandableSection title="Sponsorship & Partnership Model" icon={Award}>
        <p className="text-slate-600 mb-6">Luxury visibility with measurable community impact.</p>
        
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="font-semibold text-slate-800 mb-3">Sponsor Value</h4>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                <span>Direct access to health system leadership, clinicians, and community advocates</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                <span>Premium brand placement aligned with "Gift of Life" impact messaging</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                <span>Content capture and post-event impact reporting for stakeholders</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Tier</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Investment</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Highlights</th>
              </tr>
            </thead>
            <tbody>
              {SPONSORSHIP_TIERS.map((tier, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                  <td className="px-4 py-3 font-medium text-slate-800">{tier.tier}</td>
                  <td className="px-4 py-3 text-emerald-600 font-semibold">{tier.investment}</td>
                  <td className="px-4 py-3 text-slate-600">{tier.highlights}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-slate-500 text-sm mt-3 italic">Note: final packages will be tailored with compliance and partner input.</p>
      </ExpandableSection>

      {/* Financial Projections */}
      <ExpandableSection title="Financial Projections (Draft)" icon={DollarSign}>
        <p className="text-slate-600 mb-6">Built to fund a marquee show while paying leadership and committee fairly.</p>
        
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl p-5 text-white">
            <p className="text-emerald-100 text-sm">Total Revenue</p>
            <p className="text-3xl font-bold">{FINANCIAL_PROJECTIONS.totals.revenue}</p>
          </div>
          <div className="bg-gradient-to-br from-rose-500 to-red-500 rounded-xl p-5 text-white">
            <p className="text-rose-100 text-sm">Total Expenses</p>
            <p className="text-3xl font-bold">{FINANCIAL_PROJECTIONS.totals.expenses}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white">
            <p className="text-amber-100 text-sm">Net Proceeds</p>
            <p className="text-3xl font-bold">{FINANCIAL_PROJECTIONS.totals.netProceeds}</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-slate-800 mb-3">Revenue</h4>
            <div className="space-y-2">
              {FINANCIAL_PROJECTIONS.revenue.map((item, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600 text-sm">{item.item}</span>
                  <span className="font-medium text-emerald-600">{item.amount}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-800 mb-3">Expenses</h4>
            <div className="space-y-2">
              {FINANCIAL_PROJECTIONS.expenses.map((item, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600 text-sm">{item.item}</span>
                  <span className="font-medium text-rose-600">{item.amount}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-amber-50 to-rose-50 rounded-xl p-4 border border-amber-200">
          <p className="text-slate-700 font-medium">
            Planned Impact Distribution: <span className="text-emerald-600">$400,000 to Kidney Journey Impact Fund</span> • <span className="text-blue-600">$41,000 reserve for governance & Year-2 planning</span>
          </p>
        </div>
      </ExpandableSection>

      {/* Committee Structure */}
      <ExpandableSection title="Committee Structure" icon={Users}>
        <p className="text-slate-600 mb-6">A small, paid leadership team supported by volunteer ambassadors.</p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-slate-800 mb-4">Paid Core Team (Year 1)</h4>
            <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold text-slate-700">Role</th>
                    <th className="text-center px-4 py-2 font-semibold text-slate-700">#</th>
                    <th className="text-right px-4 py-2 font-semibold text-slate-700">Comp</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-white">
                    <td className="px-4 py-2 text-slate-700">Event Creator / Executive Producer</td>
                    <td className="px-4 py-2 text-center">1</td>
                    <td className="px-4 py-2 text-right font-medium text-emerald-600">$100,000</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-2 text-slate-700">Project Manager</td>
                    <td className="px-4 py-2 text-center">1</td>
                    <td className="px-4 py-2 text-right font-medium text-emerald-600">$75,000</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-4 py-2 text-slate-700">Committee Leads</td>
                    <td className="px-4 py-2 text-center">10</td>
                    <td className="px-4 py-2 text-right font-medium text-emerald-600">$7,500 each</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-2 text-slate-700">Event Staff (day-of + admin)</td>
                    <td className="px-4 py-2 text-center">10</td>
                    <td className="px-4 py-2 text-right font-medium text-emerald-600">$2,500 each</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-800 mb-4">Committee Lead Functions (10)</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                "Sponsorship & corporate relations",
                "Community & church partnerships",
                "Clinical liaison (dialysis + transplant)",
                "Entertainment & creative direction",
                "Guest experience + VIP protocol"
              ].map((fn, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                  <span>{fn}</span>
                </div>
              ))}
            </div>
            
            <h4 className="font-semibold text-slate-800 mt-6 mb-3">Volunteer Ambassadors (optional)</h4>
            <ul className="space-y-1 text-sm text-slate-600">
              <li>Table captains for ticket sales</li>
              <li>Community outreach volunteers</li>
              <li>Day-of support (registration, ushering)</li>
            </ul>
          </div>
        </div>
      </ExpandableSection>

      {/* Timeline */}
      <ExpandableSection title="Timeline to Launch" icon={Clock}>
        <p className="text-slate-600 mb-6">A 9–12 month runway for a signature, high-production first year.</p>
        
        <div className="grid md:grid-cols-4 gap-4">
          {TIMELINE.map((phase, index) => (
            <div key={index} className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-5 border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </div>
                <span className="text-slate-500 text-sm">{phase.phase}</span>
              </div>
              <h4 className="font-bold text-slate-800 mb-3">{phase.title}</h4>
              <ul className="space-y-2">
                {phase.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </ExpandableSection>

      {/* Ethical Firewall */}
      <ExpandableSection title="The Ethical Firewall (Critical)" icon={Shield} badgeText="IMPORTANT">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h4 className="font-semibold text-slate-800 mb-3">Why It Matters</h4>
          <ul className="space-y-2 mb-6 text-slate-700">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
              <span>Protects institutional integrity and public trust</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
              <span>Prevents any perception of pay-to-play procurement influence</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
              <span>Creates repeatable governance for future years and partners</span>
            </li>
          </ul>
          
          <h4 className="font-semibold text-slate-800 mb-3">Operating Rules (Simple & Strict)</h4>
          <ul className="space-y-2 mb-6 text-slate-700">
            <li className="flex items-start gap-2">
              <Scale className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
              <span>Separate bank account + transparent ledger for the event</span>
            </li>
            <li className="flex items-start gap-2">
              <Scale className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
              <span>Written conflict-of-interest disclosures for committee & sponsors</span>
            </li>
            <li className="flex items-start gap-2">
              <Scale className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
              <span>Charitable proceeds distributed through a defined, documented impact plan</span>
            </li>
          </ul>
          
          <div className="bg-white rounded-lg p-4 border border-amber-200">
            <h5 className="font-medium text-slate-800 mb-2">What We Need from PR Counsel</h5>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>Language that positions giving as "Gift of Life" philanthropy</li>
              <li>Talking points that keep business and charity in separate lanes</li>
              <li>Partner messaging for both executive leadership and community audiences</li>
            </ul>
          </div>
        </div>
      </ExpandableSection>

      {/* The Diversity Mandate */}
      <ExpandableSection title="The Diversity Mandate" icon={Globe}>
        <p className="text-slate-700 mb-4">
          The communications strategy must speak to <span className="font-semibold">two audiences at once:</span>
        </p>
        
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-5 border border-blue-100">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-3">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <h5 className="font-semibold text-slate-800 mb-2">The C-Suite of the Texas Medical Center</h5>
            <p className="text-slate-600 text-sm">Innovation, leadership, outcomes</p>
          </div>
          
          <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-xl p-5 border border-rose-100">
            <div className="w-10 h-10 rounded-lg bg-rose-500 flex items-center justify-center mb-3">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h5 className="font-semibold text-slate-800 mb-2">The Community</h5>
            <p className="text-slate-600 text-sm">The pews, community halls, and dialysis centers of Greater Houston—access, dignity, survival</p>
          </div>
        </div>
        
        <div className="bg-slate-100 rounded-xl p-4 text-center">
          <p className="text-slate-700 font-medium">
            This event succeeds only if it resonates at both levels simultaneously.
          </p>
        </div>
      </ExpandableSection>

      {/* Call to Action */}
      <ExpandableSection title="Founding Committee — Call to Action" icon={Target} defaultOpen={true}>
        <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-xl p-6 border border-rose-200 mb-6">
          <p className="text-rose-800 font-medium text-lg italic">
            "We are building a signature cultural event that treats kidney patients and families with celebration, dignity, and measurable impact."
          </p>
        </div>
        
        <h4 className="font-semibold text-slate-800 mb-4">What We Need Now</h4>
        <div className="space-y-3">
          {[
            "Confirm venue selection and lock the date",
            "Approve governance and ethical firewall language",
            "Open sponsor outreach: health systems, manufacturers, and community partners",
            "Finalize entertainment shortlist and booking strategy"
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">{i + 1}</span>
              </div>
              <span className="text-slate-700 font-medium">{item}</span>
            </div>
          ))}
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-5 text-white text-center">
          <p className="font-bold text-lg mb-2">Next Meeting</p>
          <p className="text-indigo-200">Approve Venue + Sponsor Deck + Governance</p>
          <p className="text-sm text-indigo-300 mt-3">ISN'T THAT DANDY! • Founder/Executive Producer • Sterile Mate LLC</p>
        </div>
      </ExpandableSection>

      {/* Closing Thought */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={DIVERSITY_IMAGES.ballroomSetup} 
            alt="Elegant Ballroom" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-rose-950/90 to-amber-950/85" />
        </div>
        
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-500/20 to-rose-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-br from-rose-500/15 to-amber-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 p-8 md:p-10 text-center">
          <Sparkles className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Closing Thought
          </h3>
          
          <p className="text-rose-100/90 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-6">
            This is not just a gala. It is a <span className="text-amber-300 font-semibold">movement of joy, culture, and medical purpose</span>—built by someone who lives the journey personally and professionally.
          </p>
          
          {/* Final Diversity Gallery */}
          <div className="grid grid-cols-5 gap-3 max-w-3xl mx-auto mb-6">
            <div className="aspect-square rounded-lg overflow-hidden opacity-80 hover:opacity-100 transition-opacity">
              <img src={DIVERSITY_IMAGES.southernBelle} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-lg overflow-hidden opacity-80 hover:opacity-100 transition-opacity">
              <img src={DIVERSITY_IMAGES.whiteFormal} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-lg overflow-hidden opacity-80 hover:opacity-100 transition-opacity">
              <img src={DIVERSITY_IMAGES.africanFormal} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-lg overflow-hidden opacity-80 hover:opacity-100 transition-opacity">
              <img src={DIVERSITY_IMAGES.asianFormal} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-lg overflow-hidden opacity-80 hover:opacity-100 transition-opacity">
              <img src={DIVERSITY_IMAGES.latinoFormal} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
          
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-amber-400/30">
            <Heart className="w-5 h-5 text-rose-400" />
            <span className="text-white font-medium">A Gift of Life</span>
          </div>
        </div>
      </div>
    </div>
  );
}
